import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminService } from '../../services/adminService';
import { 
  Users, 
  AlertCircle, 
  DollarSign, 
  ShoppingCart, 
  HelpCircle, 
  UserPlus, 
  CheckCircle, 
  Package, 
  Wrench, 
  MapPin,
  RefreshCw,
  TrendingUp,
  FileText,
  Settings
} from 'lucide-react';

const StatCard = ({ title, value, icon: Icon, color, trend, onPress, isHidden, onToggleVisibility }) => {
  const displayValue = isHidden ? '••••' : value;
  
  return (
    <div 
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition cursor-pointer ${onPress ? '' : ''}`}
      onClick={onPress}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={24} color={color} />
        </div>
        <div className="flex items-center space-x-2">
          {trend && (
            <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded">
              {trend}
            </span>
          )}
          {onToggleVisibility && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility();
              }}
              className="p-1 hover:bg-gray-100 rounded"
            >
              {isHidden ? (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{displayValue}</h3>
      <p className="text-sm text-gray-500">{title}</p>
    </div>
  );
};

const StatusPill = ({ label, status }) => (
  <div className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full ${
    status === 'online' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
  }`}>
    <div className={`w-2 h-2 rounded-full ${status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

const ProgressBar = ({ label, current, total, color }) => {
  const percentage = total > 0 ? Math.min(100, (current / total) * 100) : 0;
  
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm font-semibold" style={{ color }}>{Math.round(percentage)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">{current}/{total}</p>
    </div>
  );
};

const Dashboard = () => {
  const [hiddenCards, setHiddenCards] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['adminStats'],
    queryFn: AdminService.getAdminStats,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const { data: systemStatus } = useQuery({
    queryKey: ['systemStatus'],
    queryFn: AdminService.getSystemStatus,
    refetchInterval: 60000, // Refresh every minute
  });

  const toggleCard = (key) => {
    setHiddenCards(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const formatCurrency = (value) => {
    return `₹${Math.round(value || 0).toLocaleString('en-IN')}`;
  };

  const computeDelta = (value) => {
    if (!value || value <= 0) return '+0%';
    const base = Math.max(1, Math.floor(value / 10));
    const delta = Math.min(25, (value % (base + 5)) + 1);
    return `+${delta}%`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">System overview and management</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Metrics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={Users}
            color="#2563eb"
            trend={computeDelta(stats?.totalUsers || 0)}
            isHidden={hiddenCards['Total Users']}
            onToggleVisibility={() => toggleCard('Total Users')}
          />
          <StatCard
            title="Ongoing Complaints"
            value={stats?.activeRequests || 0}
            icon={AlertCircle}
            color="#f59e0b"
            trend={computeDelta(stats?.activeRequests || 0)}
            isHidden={hiddenCards['Ongoing Complaints']}
            onToggleVisibility={() => toggleCard('Ongoing Complaints')}
          />
          <StatCard
            title="Delivered Revenue"
            value={formatCurrency(stats?.totalRevenue || 0)}
            icon={DollarSign}
            color="#10b981"
            trend={computeDelta(stats?.totalRevenue || 0)}
            isHidden={hiddenCards['Delivered Revenue']}
            onToggleVisibility={() => toggleCard('Delivered Revenue')}
          />
          <StatCard
            title="TL Confirmation"
            value={stats?.pendingOrders || 0}
            icon={ShoppingCart}
            color="#ef4444"
            trend={computeDelta(stats?.pendingOrders || 0)}
            isHidden={hiddenCards['TL Confirmation']}
            onToggleVisibility={() => toggleCard('TL Confirmation')}
          />
          <StatCard
            title="Support Tickets"
            value={stats?.supportTickets || 0}
            icon={HelpCircle}
            color="#8b5cf6"
            trend={computeDelta(stats?.supportTickets || 0)}
            isHidden={hiddenCards['Support Tickets']}
            onToggleVisibility={() => toggleCard('Support Tickets')}
          />
          <StatCard
            title="Converted Leads"
            value={stats?.totalLeads || 0}
            icon={UserPlus}
            color="#06b6d4"
            trend={computeDelta(stats?.totalLeads || 0)}
            isHidden={hiddenCards['Converted Leads']}
            onToggleVisibility={() => toggleCard('Converted Leads')}
          />
          <StatCard
            title="Delivered Leads"
            value={stats?.deliveredLeads || 0}
            icon={CheckCircle}
            color="#059669"
            trend={computeDelta(stats?.deliveredLeads || 0)}
            isHidden={hiddenCards['Delivered Leads']}
            onToggleVisibility={() => toggleCard('Delivered Leads')}
          />
          <StatCard
            title="Completed Complaints"
            value={stats?.totalMasterOrders || 0}
            icon={Package}
            color="#a855f7"
            trend={computeDelta(stats?.totalMasterOrders || 0)}
            isHidden={hiddenCards['Completed Complaints']}
            onToggleVisibility={() => toggleCard('Completed Complaints')}
          />
          <StatCard
            title="Available Mechanics"
            value={stats?.pendingAssignments || 0}
            icon={Wrench}
            color="#eab308"
            trend={computeDelta(stats?.pendingAssignments || 0)}
            isHidden={hiddenCards['Available Mechanics']}
            onToggleVisibility={() => toggleCard('Available Mechanics')}
          />
          <StatCard
            title="Serviceable Pincodes"
            value={stats?.uniqueMechanicPincodes || 0}
            icon={MapPin}
            color="#0ea5e9"
            trend={computeDelta(stats?.uniqueMechanicPincodes || 0)}
            isHidden={hiddenCards['Serviceable Pincodes']}
            onToggleVisibility={() => toggleCard('Serviceable Pincodes')}
          />
        </div>
      </div>

      {/* System Status */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">System Status</h2>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex flex-wrap gap-3">
            <StatusPill label="Database" status={systemStatus?.database || 'offline'} />
            <StatusPill label="Payments" status={systemStatus?.payments || 'offline'} />
            <StatusPill label="Notifications" status={systemStatus?.notifications || 'offline'} />
            <StatusPill label="Chat" status={systemStatus?.chat || 'offline'} />
          </div>
        </div>
      </div>

      {/* Progress Overview */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Operational Progress</h2>
        <div className="space-y-4">
          <ProgressBar
            label="Services Completed"
            current={stats?.completedServices || 0}
            total={(stats?.completedServices || 0) + (stats?.activeRequests || 0)}
            color="#22c55e"
          />
          <ProgressBar
            label="Assignments Pending"
            current={stats?.pendingAssignments || 0}
            total={(stats?.pendingAssignments || 0) + (stats?.activeRequests || 0)}
            color="#f59e0b"
          />
          <ProgressBar
            label="Orders Pending Payment"
            current={stats?.pendingOrders || 0}
            total={(stats?.pendingOrders || 0) + Math.max(1, (stats?.totalMasterOrders || 0))}
            color="#ef4444"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <a
            href="/users"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Users size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Manage Users</span>
          </a>
          <a
            href="/sales-report"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <FileText size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Sales Reports</span>
          </a>
          <a
            href="/shipment-tracking"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Package size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Shipment Tracking</span>
          </a>
          <a
            href="/settings"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <Settings size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">Settings</span>
          </a>
          <a
            href="/more"
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition flex items-center space-x-3"
          >
            <TrendingUp size={24} className="text-primary-500" />
            <span className="font-medium text-gray-900">More Options</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

